/**
 * ESG SaaS Platform - API Configuration
 *
 * Base configuration for all API calls to the FastAPI backend.
 * Uses Supabase Auth for token management.
 */

import { supabase } from './supabaseClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
  headers?: Record<string, string>;
  skipAuth?: boolean;
  _isRetry?: boolean;
}

interface ApiResponse<T> {
  data: T;
  status: number;
  ok: boolean;
}

class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Token management - reads from Supabase session
export const tokenManager = {
  getAccessToken: async (): Promise<string | null> => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  },

  isAuthenticated: (): boolean => {
    // Synchronous check using Supabase's localStorage key
    const storageKey = `sb-${(process.env.REACT_APP_SUPABASE_URL || '').replace(/https?:\/\//, '').split('.')[0]}-auth-token`;
    return !!localStorage.getItem(storageKey);
  },

  clearTokens: async (): Promise<void> => {
    await supabase.auth.signOut();
  },
};

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, skipAuth = false, _isRetry = false } = options;

  // Add authorization header from Supabase session
  const authHeaders: Record<string, string> = {};
  if (!skipAuth) {
    const token = await tokenManager.getAccessToken();
    if (token) {
      authHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...headers,
    },
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  let data;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    // Handle 401 Unauthorized - try refreshing session and retrying once
    if (response.status === 401 && !skipAuth && !_isRetry) {
      const { error } = await supabase.auth.refreshSession();
      if (!error) {
        // Refresh succeeded - retry the original request exactly once
        return request<T>(endpoint, { method, body, headers, skipAuth, _isRetry: true });
      }
      // Refresh failed - sign out
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    throw new ApiError(
      data?.detail || 'An error occurred',
      response.status,
      data
    );
  }

  return data as T;
}

export const api = {
  get: <T>(endpoint: string, headers?: Record<string, string>) =>
    request<T>(endpoint, { method: 'GET', headers }),

  post: <T>(endpoint: string, body?: any, headers?: Record<string, string>) =>
    request<T>(endpoint, { method: 'POST', body, headers }),

  put: <T>(endpoint: string, body?: any, headers?: Record<string, string>) =>
    request<T>(endpoint, { method: 'PUT', body, headers }),

  delete: <T>(endpoint: string, headers?: Record<string, string>) =>
    request<T>(endpoint, { method: 'DELETE', headers }),

  // Version without auth for public endpoints
  postPublic: <T>(endpoint: string, body?: any) =>
    request<T>(endpoint, { method: 'POST', body, skipAuth: true }),

  getPublic: <T>(endpoint: string) =>
    request<T>(endpoint, { method: 'GET', skipAuth: true }),

  // FormData POST for file uploads
  postFormData: async <T>(endpoint: string, formData: FormData): Promise<T> => {
    const token = await tokenManager.getAccessToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    let data;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      throw new ApiError(
        data?.detail || 'An error occurred',
        response.status,
        data
      );
    }

    return data as T;
  },

  // GET blob for file downloads
  getBlob: async (endpoint: string): Promise<{ blob: Blob; filename: string | null }> => {
    const token = await tokenManager.getAccessToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      let data;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      throw new ApiError(
        data?.detail || 'An error occurred',
        response.status,
        data
      );
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename: string | null = null;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match) {
        filename = match[1].replace(/['"]/g, '');
      }
    }

    const blob = await response.blob();
    return { blob, filename };
  },
};

export { ApiError, API_BASE_URL };
export type { ApiResponse };
